import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import BottomBanner from "../../components/banners/BottomBanner";
import StepNavigation from "../../components/essentials/step-navigation/StepNavigation";
import Header from "../../components/header/Header";
import "./SignUp.scss";

const SignUp: React.FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const location = useLocation();

	// Define the sign-up steps
	const stepRoutes = useMemo(
		() => [
			{ path: "/sign-up/email", title: t("signUp.steps.email", "Email") },
			{
				path: "/sign-up/code",
				title: t("signUp.steps.verification", "Verification"),
			},
			{
				path: "/sign-up/password",
				title: t("signUp.steps.password", "Password"),
			},
		],
		[t]
	);

	// Determine current step based on location
	const getCurrentStep = () => {
		const currentPath = location.pathname;
		const stepIndex = stepRoutes.findIndex((route) =>
			currentPath.includes(route.path)
		);
		return stepIndex !== -1 ? stepIndex + 1 : 1;
	};

	const currentStep = getCurrentStep();

	// Handle step navigation - disabled
	const handleStepClick = (step: number) => {
		// Navigation to previous steps is disabled
		return;
	};

	// Handle next button
	const handleNext = () => {
		// For step 1 (email), trigger form submission
		if (currentStep === 1) {
			const form = document.getElementById(
				"sign-up-email-form"
			) as HTMLFormElement;
			if (form) {
				form.requestSubmit();
			}
			return;
		}

		// For other steps, just navigate
		if (currentStep < stepRoutes.length) {
			navigate(stepRoutes[currentStep].path);
		}
	};

	// Handle submit button (on last step)
	const handleSubmit = () => {
		const form = document.getElementById(
			"set-password-form"
		) as HTMLFormElement;
		if (form) {
			form.requestSubmit();
		}
		return;
	};

	// Get visited steps (all steps up to current step)
	const visitedSteps = useMemo(() => {
		const visited = new Set<number>();
		for (let i = 1; i <= currentStep; i++) {
			visited.add(i);
		}
		return visited;
	}, [currentStep]);

	// Simple completion status - completed if we've passed the step
	const getStepCompletionStatus = (
		step: number
	): "complete" | "partial" | "incomplete" => {
		if (step < currentStep) return "complete";
		if (step === currentStep) return "incomplete";
		return "incomplete";
	};

	const hasStepData = (step: number): boolean => {
		return step < currentStep;
	};

	// Redirect to first step if on base sign-up route
	useEffect(() => {
		if (location.pathname === "/sign-up" || location.pathname === "/sign-up/") {
			navigate("/sign-up/email");
		}
	}, [location.pathname, navigate]);

	// Hide stepper on password recovery flow and email verification
	const shouldShowStepper =
		!location.pathname.includes("password-recovery") &&
		!location.pathname.includes("email-verification") &&
		location.pathname !== "/sign-up" &&
		location.pathname !== "/sign-up/";

	// Hide navigation buttons on step 2 (code verification)
	const shouldShowNavigationButtons = currentStep !== 2;

	return (
		<>
			<div className="container-fluid h-100 p-0 sign-up ">
				<div className="row h-100">
					{/* Colonna immagine */}
					<div className="col-lg-6 d-none d-lg-flex align-items-center justify-content-center col-image"></div>
					{/* Colonna form */}
					<div className="col-lg-6">
						<div className="sign-up-wrapper">
							<Header className="sign-up__header login__caption-title">
								{t("signUp.headerTitle")}
							</Header>

							{shouldShowStepper ? (
								<StepNavigation
									currentStep={currentStep}
									totalSteps={stepRoutes.length}
									stepTitles={stepRoutes.map((route) => route.title)}
									onStepClick={handleStepClick}
									visitedSteps={visitedSteps}
									hasStepData={hasStepData}
									getStepCompletionStatus={getStepCompletionStatus}
									showNavigationButtons={shouldShowNavigationButtons}
									onNext={handleNext}
									onSubmit={handleSubmit}
									nextLabel={t("common.buttons.next", "Next")}
									prevLabel={t("common.buttons.previous", "Previous")}
									submitLabel={t("common.buttons.submit", "Submit")}
								>
									<Outlet />
								</StepNavigation>
							) : (
								<div className="sign-up__content">
									<Outlet />
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			<BottomBanner
				value={t("signUp.bannerText")}
				link={t("signUp.bannerLink")}
				action={() => navigate("/login")}
			></BottomBanner>
		</>
	);
};

export default SignUp;
